import { useEffect, useMemo, useState } from 'react';
import {
  periodStanding,
  JOB_CADENCES,
  JOB_CADENCE_BLURBS,
  JOB_CADENCE_LABELS,
  periodLabel,
  periodOf,
  shiftPeriod,
  studentName,
  type Job,
  type JobBoard,
  type JobCadence,
  type JobHolder,
  type Student,
} from '@td/shared';
import {
  useAssignJob,
  useClearJobs,
  useCreateJob,
  useDeleteJob,
  useFillJobs,
  useJobBoard,
  useJobs,
  useStudentJobHistory,
  useUnassignJob,
  useUpdateJob,
  type JobInput,
} from '../hooks/useJobs';
import { useStudents } from '../hooks/useStudents';
import { useDragOnto } from '../lib/useDragOnto';
import { StudentAvatar } from '../components/StudentAvatar';
import { HeaderMenu } from '../components/HeaderMenu';
import { Modal } from '../components/Modal';
import { Portal } from '../components/Portal';
import { useConfirm } from '../components/ConfirmDialog';
import { Doodle } from '../components/Doodle';

/**
 * Classroom jobs.
 *
 * The board by the door, with the argument taken out of it. Every child's tile
 * carries how many jobs they have had this year, and a name dropped onto a job
 * they have had recently is flagged rather than silently allowed — because the
 * complaint this page exists to answer is always "she's had it twice and I
 * haven't had it at all".
 *
 * Weekly and monthly are two boards on one page rather than two pages: they
 * are the same act, done to a different clock, and the child who has had four
 * weekly jobs and no monthly one is only visible if both are in front of you.
 */

const CADENCE_STORE = 'td.jobsCadence';

export function JobsPage() {
  const [cadence, setCadence] = useState<JobCadence>(
    () => (localStorage.getItem(CADENCE_STORE) as JobCadence | null) ?? 'weekly',
  );
  const [period, setPeriod] = useState(() => periodOf(cadence));

  const { data: board, isLoading } = useJobBoard(cadence, period);
  const { data: students = [] } = useStudents();
  const { data: jobs = [] } = useJobs();

  const [managing, setManaging] = useState(false);
  const [showing, setShowing] = useState<Student | null>(null);
  const [printing, setPrinting] = useState(false);

  const assign = useAssignJob();
  const unassign = useUnassignJob();
  const fill = useFillJobs();
  const clear = useClearJobs();
  const confirm = useConfirm();

  const byId = useMemo(() => new Map(students.map((s) => [s.id, s])), [students]);

  function switchTo(next: JobCadence) {
    setCadence(next);
    localStorage.setItem(CADENCE_STORE, next);
    // The period is in the other clock's units, so it cannot carry across.
    setPeriod(periodOf(next));
  }

  /*
   * A name dropped on a job card. The target id is the job's; 'roster' is the
   * list on the left, and dropping there takes the child off whatever they
   * were on.
   */
  const drag = useDragOnto<{ student: Student; fromJobId: string | null }>((dropped, target) => {

    if (target === 'roster') {
      if (dropped.fromJobId) {
        unassign.mutate({ jobId: dropped.fromJobId, studentId: dropped.student.id, period });
      }
      return;
    }

    if (target === dropped.fromJobId) return;

    /*
     * Moved, not copied. Dragging from one job to another takes them off the
     * first — otherwise a teacher tidying the board quietly doubles somebody's
     * count, and the number under their name is the whole point of this page.
     */
    if (dropped.fromJobId) {
      unassign.mutate({ jobId: dropped.fromJobId, studentId: dropped.student.id, period });
    }
    assign.mutate({ jobId: target, studentId: dropped.student.id, period });
  });

  /*
   * Every turn can be arranged, not only this one.
   *
   * The board used to be read-only unless the period was the current one, and
   * "not current" was taken to mean the past — so stepping forward to next
   * week gave a board labelled "Looking back" that refused every drop. Setting
   * next week up on a Friday is the ordinary way to do this; doing it on
   * Monday morning with the register open is the exception.
   *
   * Last week is editable too. Nothing is protected by refusing it — she can
   * already clear a whole turn — and "you cannot correct Tuesday" is its own
   * bug. The header says which turn is on screen, which is the honest guard.
   */
  const standing = periodStanding(cadence, period);
  const current = standing === 'current';
  const unplaced = (board?.standing ?? []).filter((s) => s.timesThisPeriod === 0);

  /*
   * The chart prints through a portal rather than through the page: the board
   * is a set of controls, the chart is a document, and making one print as
   * the other means a print rule on every button on screen.
   */
  useEffect(() => {
    if (!printing) return;

    // A frame for the portal to paint before the print dialog freezes it.
    const id = window.setTimeout(() => {
      window.print();
      setPrinting(false);
    }, 60);

    return () => window.clearTimeout(id);
  }, [printing]);

  return (
    <div className={`space-y-4 ${printing ? 'print:hidden' : ''}`}>
      <header className="page-header flex flex-wrap items-end justify-between gap-3">
        <h1 className="text-2xl">Student jobs</h1>

        <div className="flex flex-wrap items-center gap-1.5">
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPeriod(shiftPeriod(cadence, period, -1))}
              className="btn-secondary px-2.5 py-1.5"
              aria-label="The turn before"
            >
              ←
            </button>
            <div className="min-w-[150px] px-2 text-center">
              <div className="text-sm text-espresso">{periodLabel(cadence, period)}</div>
              <div className="text-[11px] text-stone">
                {standing === 'current'
                  ? 'This one'
                  : standing === 'future'
                    ? 'Coming up'
                    : 'Already been'}
              </div>
            </div>
            <button
              onClick={() => setPeriod(shiftPeriod(cadence, period, 1))}
              className="btn-secondary px-2.5 py-1.5"
              aria-label="The turn after"
            >
              →
            </button>
            {!current && (
              <button onClick={() => setPeriod(periodOf(cadence))} className="btn-secondary ml-1.5">
                Now
              </button>
            )}
          </div>

          <button
            className="btn-primary"
            disabled={fill.isPending}
            onClick={() => fill.mutate({ cadence, period })}
            title="Gives every empty job to whoever has had it least often"
          >
            {fill.isPending ? 'Filling…' : 'Fill the rest'}
          </button>

          <button className="btn-secondary" onClick={() => setPrinting(true)}>
            Print the chart
          </button>

          <HeaderMenu label="Job settings">
            {(close) => (
              <div className="space-y-1.5">
                <button
                  className="btn-soft w-full"
                  onClick={() => {
                    setManaging(true);
                    close();
                  }}
                >
                  Add or change a job
                </button>

                <button
                  className="btn-soft-danger w-full"
                  onClick={async () => {
                    close();
                    const ok = await confirm({
                      title: `Clear the ${JOB_CADENCE_LABELS[cadence].toLowerCase()} board?`,
                      body: 'Every name comes off this turn. What they did in earlier turns is kept.',
                      confirmLabel: 'Clear it',
                      danger: true,
                    });
                    if (ok) clear.mutate({ cadence, period });
                  }}
                >
                  Take everyone off this turn
                </button>
              </div>
            )}
          </HeaderMenu>
        </div>
      </header>

      {/* The two boards. */}
      <div className="flex flex-wrap items-center gap-1.5">
        {JOB_CADENCES.map((c) => (
          <button
            key={c}
            onClick={() => switchTo(c)}
            aria-pressed={c === cadence}
            className={c === cadence ? 'btn-primary' : 'btn-soft'}
          >
            {JOB_CADENCE_LABELS[c]}
          </button>
        ))}
        <span className="ml-1 text-xs text-stone">{JOB_CADENCE_BLURBS[cadence]}</span>
      </div>

      {board?.needsSetting && (
        <p className="rounded-card border border-terracotta bg-linen/60 px-3 py-2 text-sm text-espresso">
          {cadence === 'weekly' ? 'A new week' : 'A new month'} has started and nobody has a job
          yet. Drag names onto the cards, or press <strong>Fill the rest</strong>.
        </p>
      )}

      {isLoading ? (
        <p className="text-sm text-stone">Loading…</p>
      ) : students.length === 0 ? (
        <div className="rounded-card border border-sand bg-white p-8 text-center">
          <Doodle name="star" className="mx-auto mb-2 h-10 w-10 text-clay" />
          <p className="text-sm text-stone">
            There are no students on this year’s roster yet. Add the class first and the jobs board
            fills itself in.
          </p>
        </div>
      ) : (
        <div className="grid items-start gap-4 lg:grid-cols-[16rem_1fr]">
          {/* ------------------------------------------------ who is spare */}
          <div
            ref={(el) => drag.registerTarget('roster', el)}
            className={[
              'rounded-card border p-3 lg:sticky lg:top-4',
              drag.over === 'roster' ? 'border-terracotta bg-linen/60' : 'border-sand bg-white',
            ].join(' ')}
          >
            <p className="label">Without a job</p>

            {unplaced.length === 0 ? (
              <p className="text-xs text-stone">Everybody has one.</p>
            ) : (
              <ul className="space-y-1">
                {unplaced.map((standing) => {
                  const student = byId.get(standing.studentId);
                  if (!student) return null;

                  return (
                    <li key={student.id}>
                      <StudentTile
                        student={student}
                        times={standing.timesAllYear}
                        soft={standing.hadLastTurn}
                        draggable
                        onPointerDown={(e) =>
                          drag.begin({ student, fromJobId: null }, e)
                        }
                        onClick={() => {
                          if (!drag.moved.current) setShowing(student);
                        }}
                      />
                    </li>
                  );
                })}
              </ul>
            )}

            <p className="mt-2 border-t border-sand pt-2 text-[11px] text-stone">
              The number is how many jobs they have had all year. Drag a name back here to take
              them off one.
            </p>
          </div>

          {/* ---------------------------------------------------- the jobs */}
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {(board?.jobs ?? []).map((job) => (
              <JobCard
                key={job.id}
                job={job}
                students={byId}
                over={drag.over === job.id}
                registerTarget={drag.registerTarget}
                onGrab={(student, e) => drag.begin({ student, fromJobId: job.id }, e)}
                onOpen={(student) => {
                  if (!drag.moved.current) setShowing(student);
                }}
                onRemove={(studentId) =>
                  unassign.mutate({ jobId: job.id, studentId, period })
                }
              />
            ))}

            {(board?.jobs.length ?? 0) === 0 && (
              <p className="text-sm text-stone">
                No {JOB_CADENCE_LABELS[cadence].toLowerCase()} jobs yet. Add one from the gear.
              </p>
            )}
          </div>
        </div>
      )}

      {/* The name under the pointer, following it. */}
      {drag.item && (
        <Portal>
          <div
            className="pointer-events-none fixed z-50 rounded-card border border-terracotta bg-white px-2.5 py-1.5 text-sm shadow-lifted"
            style={{ left: drag.at.x + 12, top: drag.at.y + 12 }}
          >
            {studentName(drag.item.student)}
          </div>
        </Portal>
      )}

      {managing && <JobManager jobs={jobs} onClose={() => setManaging(false)} />}

      {showing && <HistoryCard student={showing} onClose={() => setShowing(null)} />}

      {printing && board && <JobChart board={board} students={byId} />}
    </div>
  );
}

/* --------------------------------------------------------------- a tile */

function StudentTile({
  student,
  times,
  flag,
  soft,
  draggable,
  onPointerDown,
  onClick,
  onRemove,
}: {
  student: Student;
  times: number;
  flag?: JobHolder['flag'];
  /** Had some job last turn — the softer of the two warnings. */
  soft?: boolean;
  draggable: boolean;
  onPointerDown: (e: React.PointerEvent) => void;
  onClick: () => void;
  onRemove?: () => void;
}) {
  /*
   * Two strengths of warning, and they look different on purpose.
   *
   * A repeat of the same job is the one that starts an argument, so it is
   * outlined in rust and says so. Having had *a* job last turn is only worth
   * a quiet dot — it is a nudge to spread things round, not a mistake.
   */
  const repeat = flag === 'repeat';
  const recent = flag === 'recent' || (!flag && soft);

  return (
    <div
      className={[
        'flex w-full items-center gap-2 rounded-card border px-2 py-1.5 text-left',
        repeat ? 'border-rust bg-rust/5' : 'border-sand bg-white',
        draggable ? 'cursor-grab active:cursor-grabbing' : '',
      ].join(' ')}
      onPointerDown={draggable ? onPointerDown : undefined}
      /*
       * On a touchscreen, a finger going up or down the list still scrolls it;
       * a finger going sideways starts a drag. `none` would make the list
       * unscrollable on the tablet, which is a worse trade than needing to
       * pull a name sideways before lifting it.
       */
      style={draggable ? { touchAction: 'pan-y' } : undefined}
    >
      <StudentAvatar student={student} size={28} />

      <button className="min-w-0 flex-1 text-left" onClick={onClick}>
        <span className="block truncate text-sm text-espresso">{studentName(student)}</span>
        <span className="block text-[11px] text-stone">
          {times === 0 ? 'No jobs yet' : `${times} job${times === 1 ? '' : 's'} this year`}
          {repeat && ' · had this one recently'}
          {!repeat && recent && ' · had a job last turn'}
        </span>
      </button>

      {recent && !repeat && (
        <span
          className="h-1.5 w-1.5 shrink-0 rounded-full bg-clay"
          title="Had a job last turn"
          aria-hidden
        />
      )}

      {onRemove && (
        <button
          // Marked so a press here never turns into a drag of the row.
          data-no-drag
          className="btn-soft-danger px-2 py-1 text-xs"
          onClick={onRemove}
          aria-label={`Take ${studentName(student)} off`}
        >
          ✕
        </button>
      )}
    </div>
  );
}

/* --------------------------------------------------------------- a card */

function JobCard({
  job,
  students,
  over,
  registerTarget,
  onGrab,
  onOpen,
  onRemove,
}: {
  job: JobBoard['jobs'][number];
  students: Map<string, Student>;
  over: boolean;
  registerTarget: (id: string, el: HTMLElement | null) => void;
  onGrab: (student: Student, e: React.PointerEvent) => void;
  onOpen: (student: Student) => void;
  onRemove: (studentId: string) => void;
}) {
  const short = job.holders.length < job.wants;

  return (
    <section
      ref={(el) => registerTarget(job.id, el)}
      className={[
        'rounded-card border-2 p-3 transition-colors',
        over
          ? 'border-terracotta bg-linen/60'
          : short
            ? 'border-dashed border-sand bg-white'
            : 'border-sand bg-white',
      ].join(' ')}
    >
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <h2 className="text-base text-espresso">{job.name}</h2>
        <span className={`text-[11px] ${short ? 'text-rust' : 'text-stone'}`}>
          {job.holders.length} of {job.wants}
        </span>
      </div>

      {job.description && <p className="mb-2 text-xs text-stone">{job.description}</p>}

      {job.holders.length === 0 ? (
        <p className="rounded-card border border-dashed border-sand px-2 py-3 text-center text-xs text-stone">
          Drag a name here
        </p>
      ) : (
        <ul className="space-y-1">
          {job.holders.map((holder) => {
            const student = students.get(holder.studentId);
            if (!student) return null;

            return (
              <li key={holder.studentId}>
                <StudentTile
                  student={student}
                  times={holder.timesOnThisJob}
                  flag={holder.flag}
                  draggable
                  onPointerDown={(e) => onGrab(student, e)}
                  onClick={() => onOpen(student)}
                  onRemove={() => onRemove(holder.studentId)}
                />
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/* ------------------------------------------------------------- a history */

function HistoryCard({ student, onClose }: { student: Student; onClose: () => void }) {
  const { data, isLoading } = useStudentJobHistory(student.id);

  /** How many times each job, so the argument is settled in one glance. */
  const counts = useMemo(() => {
    const by = new Map<string, number>();
    for (const turn of data?.turns ?? []) by.set(turn.jobName, (by.get(turn.jobName) ?? 0) + 1);
    return [...by.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [data]);

  return (
    <Modal
      onClose={onClose}
      label={`${studentName(student)}’s jobs`}
      className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-card bg-cream p-5 shadow-lifted"
    >
      <div className="mb-3 flex items-center gap-3">
        <StudentAvatar student={student} size={44} />
        <div>
          <h2 className="text-lg">{studentName(student)}</h2>
          <p className="text-xs text-stone">
            {data?.total ?? 0} job{data?.total === 1 ? '' : 's'} this year
          </p>
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-stone">Loading…</p>
      ) : (data?.total ?? 0) === 0 ? (
        <p className="text-sm text-stone">No jobs yet this year.</p>
      ) : (
        <>
          <ul className="mb-3 flex flex-wrap gap-1.5">
            {counts.map(([name, count]) => (
              <li
                key={name}
                className="rounded-full border border-sand bg-white px-2.5 py-1 text-xs text-espresso"
              >
                {name} · {count}
              </li>
            ))}
          </ul>

          <ol className="space-y-1">
            {data!.turns.map((turn, i) => (
              <li
                key={`${turn.jobId}-${turn.period}-${i}`}
                className="flex items-baseline justify-between gap-3 border-b border-sand py-1 text-sm last:border-0"
              >
                <span className="text-espresso">{turn.jobName}</span>
                <span className="text-xs text-stone">{periodLabel(turn.cadence, turn.period)}</span>
              </li>
            ))}
          </ol>
        </>
      )}

      <div className="mt-4">
        <button className="btn-secondary" onClick={onClose}>
          Close
        </button>
      </div>
    </Modal>
  );
}

/* --------------------------------------------------------- the job list */

function JobManager({ jobs, onClose }: { jobs: Job[]; onClose: () => void }) {
  const create = useCreateJob();
  const update = useUpdateJob();
  const remove = useDeleteJob();
  const confirm = useConfirm();

  const [draft, setDraft] = useState<JobInput>({
    name: '',
    cadence: 'weekly',
    description: '',
    wants: 1,
  });

  return (
    <Modal
      onClose={onClose}
      label="The jobs"
      className="max-h-[88vh] w-full max-w-2xl overflow-y-auto rounded-card bg-cream p-5 shadow-lifted"
    >
      <h2 className="mb-3 text-lg">The jobs in this room</h2>

      <ul className="space-y-2">
        {jobs.map((job) => (
          <li key={job.id} className="rounded-card border border-sand bg-white p-2.5">
            <div className="flex flex-wrap items-center gap-2">
              <input
                className="field min-w-[9rem] flex-1 py-1 text-sm"
                value={job.name}
                maxLength={60}
                onChange={(e) => update.mutate({ id: job.id, name: e.target.value })}
                aria-label="Job name"
              />

              <select
                className="field w-auto py-1 text-sm"
                value={job.cadence}
                onChange={(e) =>
                  update.mutate({ id: job.id, cadence: e.target.value as JobCadence })
                }
                aria-label="How often it changes"
              >
                {JOB_CADENCES.map((c) => (
                  <option key={c} value={c}>
                    {JOB_CADENCE_LABELS[c]}
                  </option>
                ))}
              </select>

              <label className="flex items-center gap-1.5 text-xs text-walnut">
                Children
                <input
                  type="number"
                  className="field w-16 py-1"
                  min={1}
                  max={30}
                  value={job.wants}
                  onChange={(e) =>
                    update.mutate({
                      id: job.id,
                      wants: Math.max(1, Math.min(30, Number(e.target.value) || 1)),
                    })
                  }
                />
              </label>

              <button
                className="btn-soft-danger px-2 py-1"
                onClick={async () => {
                  const ok = await confirm({
                    title: `Delete ${job.name}?`,
                    body: 'Every turn anyone has had on it goes too. The other jobs are untouched.',
                    confirmLabel: 'Delete',
                    danger: true,
                  });
                  if (ok) remove.mutate(job.id);
                }}
              >
                Delete
              </button>
            </div>

            <input
              className="field mt-1.5 py-1 text-sm"
              placeholder="What the child actually does — printed on the wall chart"
              value={job.description}
              maxLength={200}
              onChange={(e) => update.mutate({ id: job.id, description: e.target.value })}
              aria-label="What the job is"
            />
          </li>
        ))}
      </ul>

      {/* ------------------------------------------------------- a new one */}
      <div className="mt-4 rounded-card border border-sand bg-linen/40 p-3">
        <p className="label">A job of your own</p>

        <div className="flex flex-wrap items-end gap-2">
          <label className="min-w-[10rem] flex-1">
            <span className="label">Name</span>
            <input
              className="field py-1"
              value={draft.name}
              maxLength={60}
              placeholder="Plant waterer"
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />
          </label>

          <label>
            <span className="label">Changes</span>
            <select
              className="field w-auto py-1"
              value={draft.cadence}
              onChange={(e) => setDraft({ ...draft, cadence: e.target.value as JobCadence })}
            >
              {JOB_CADENCES.map((c) => (
                <option key={c} value={c}>
                  {JOB_CADENCE_LABELS[c]}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span className="label">Children</span>
            <input
              type="number"
              className="field w-20 py-1"
              min={1}
              max={30}
              value={draft.wants}
              onChange={(e) =>
                setDraft({ ...draft, wants: Math.max(1, Math.min(30, Number(e.target.value) || 1)) })
              }
            />
          </label>

          <button
            className="btn-primary"
            disabled={!draft.name.trim() || create.isPending}
            onClick={() =>
              create.mutate(
                { ...draft, name: draft.name.trim() },
                {
                  onSuccess: () =>
                    setDraft({ name: '', cadence: draft.cadence, description: '', wants: 1 }),
                },
              )
            }
          >
            Add it
          </button>
        </div>

        <input
          className="field mt-2 py-1 text-sm"
          placeholder="What the child does"
          value={draft.description}
          maxLength={200}
          onChange={(e) => setDraft({ ...draft, description: e.target.value })}
        />
      </div>

      <div className="mt-4">
        <button className="btn-secondary" onClick={onClose}>
          Done
        </button>
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------------- the wall */

/**
 * The chart that gets stuck by the door.
 *
 * Big names, no counts, no flags. This one is for the children — the numbers
 * on the board are for the teacher, and a six-year-old reading "had this one
 * recently" next to their own name is not what any of this is for.
 */
function JobChart({ board, students }: { board: JobBoard; students: Map<string, Student> }) {
  return (
    /*
     * Painted only for the printer, never on screen — the same way the sub
     * packet prints. An on-screen preview would be one more overlay to escape
     * from, and there is nothing here to check before printing that the board
     * behind it does not already show.
     */
    <Portal>
      <div className="hidden print:block">
        <div className="print-sheet bg-white p-8">
          <header className="mb-6 border-b-2 border-espresso pb-2 text-center">
            <h1 className="font-display text-3xl">Our classroom jobs</h1>
            <p className="text-sm">
              {JOB_CADENCE_LABELS[board.cadence]} · {periodLabel(board.cadence, board.period)}
            </p>
          </header>

          <div className="grid grid-cols-2 gap-x-8 gap-y-5">
            {board.jobs.map((job) => (
              <section key={job.id} className="break-inside-avoid">
                <h2 className="border-b border-sand pb-1 font-display text-xl">{job.name}</h2>

                {job.holders.length === 0 ? (
                  <p className="pt-1 text-lg text-stone">—</p>
                ) : (
                  <ul className="pt-1">
                    {job.holders.map((h) => {
                      const student = students.get(h.studentId);
                      return student ? (
                        <li key={h.studentId} className="text-xl leading-relaxed">
                          {studentName(student)}
                        </li>
                      ) : null;
                    })}
                  </ul>
                )}
              </section>
            ))}
          </div>
        </div>
      </div>
    </Portal>
  );
}
